"use client";

import { Icons } from "@/components/icons";
import { FEATURES, type FeatureItem } from "../_data/page-data";
import {
  AnimatedSection,
  StaggerGrid,
  FadeInChild,
} from "./motion";

/* ─── Feature icon resolver ─── */
function FeatureIcon({ f }: { f: FeatureItem }) {
  if (f.svgPath) {
    return (
      <svg className="h-6 w-6" viewBox="0 0 256 260" fill="currentColor">
        <path d={f.svgPath} />
      </svg>
    );
  }
  const Ic = Icons[f.iconKey as keyof typeof Icons];
  return <Ic className="h-6 w-6" />;
}

export function FeaturesSection() {
  return (
    <AnimatedSection id="features" className="container space-y-8 py-16 md:py-24 lg:py-32">
      <div className="mx-auto flex max-w-[58rem] flex-col items-center space-y-4 text-center">
        <span className="inline-flex items-center rounded-full bg-purple-50 px-4 py-1 text-xs font-semibold text-purple-600 ring-1 ring-inset ring-purple-200">
          Features
        </span>
        <h2 className="font-heading text-3xl leading-[1.1] sm:text-3xl md:text-6xl">
          Everything you need to <span className="text-gradient">build with AI</span>
        </h2>
        <p className="max-w-[85%] leading-relaxed text-muted-foreground sm:text-lg">
          Chatbots, websites, workflows, analytics and integrations — all in one platform powered by
          the best AI models.
        </p>
      </div>

      <StaggerGrid className="mx-auto grid justify-center gap-6 sm:grid-cols-2 md:max-w-[72rem] md:grid-cols-3 lg:grid-cols-4 mt-8">
        {FEATURES.map((f) => (
          <FadeInChild key={f.title}>
            <div className="group relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white/80 backdrop-blur-sm p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/60">
              <div
                className={`absolute top-0 right-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full ${f.glow} transition-transform duration-500 group-hover:scale-[2]`}
              />
              <div className="relative flex h-[180px] flex-col justify-between">
                <div className="flex items-center gap-2">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${f.bg} transition-transform group-hover:scale-110`}>
                    <FeatureIcon f={f} />
                  </div>
                  {f.isNew && (
                    <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-violet-600 px-2 py-0.5 text-[9px] font-bold text-white uppercase tracking-wider shadow-sm">
                      New
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="font-bold text-lg">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </div>
            </div>
          </FadeInChild>
        ))}
      </StaggerGrid>
    </AnimatedSection>
  );
}
